import {
  Dispatch,
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from 'react';

import { Button, message, Select, Input, Checkbox, InputNumber } from 'antd';
import debounce from 'lodash/debounce';
import moment from 'moment';
import { T } from 'react-translator-component';
import styled from 'styled-components';

import { getStartOcppTags } from '../../hooks/ocpptags';
import { getUserPrimaryCard } from '../../hooks/pagSeguro';
import {
  getChargeboxTask,
  setChangeAvailability,
  setGetConfiguration,
  setRemoteStartTransaction,
  setRemoteStopTransaction,
  setReset,
  setSetChargingProfile,
  setUnlockConnector,
  setUpdateFirmware,
  setTriggerMessage,
  setChangeConfiguration,
  setGetDiagnostics,
  setClearCache,
  setClearChargingProfile,
  setDataTransfer,
  setSendLocalList,
  setGetCompositeSchedule,
  setReserveNow,
} from '../../hooks/operations';
import { getPaymentTypes } from '../../hooks/paymentTypes';
import { isMonetizationProUser } from '../../utils/isMonetizationProUser/isMonetizationProUser';
import { isTerpelTenant } from '../../utils/isTerpelTenant/isTerpelTenant';
import { COLORS } from '../../utils/theme/colors';
import DatePickerCustom from '../atoms/DatePickerCustom';
import TextArea from 'antd/lib/input/TextArea';

import SearchOcppTagOrDocument, {
  IdentificationType,
} from '../molecules/SearchOcppTagOrDocument/SearchOcppTagOrDocument';

const { Option } = Select;

interface IOperationsExecute {
  mode: string;
  targetKeys: any;
  dataSource: any;
  menuKey: any;
  setOperationResultSource: Dispatch<any>;
  setIsExecuted: Dispatch<any>;
}

const START = moment().format('YYYY-MM-DD HH:mm:ss');

const DEBOUNCE_TIMEOUT = 500;

const OperationsExecute = ({
  mode,
  targetKeys,
  dataSource,
  menuKey,
  setOperationResultSource,
  setIsExecuted,
}: IOperationsExecute) => {
  const [availabilityTypeInput, setAvailabilityTypeInput] = useState<any>('');
  const [connectorIdInput, setConnectorIdInput] = useState<any>('');
  const [durationInput, setDurationInput] = useState<any>('');
  const [chargingRateUnitInput, setChargingRateUnitInput] = useState<any>('');
  const [directoryURIInput, setDirectoryURIInput] = useState('');
  const [retriesInput, setRetriesInput] = useState('1');
  const [retryIntervalInput, setRetryIntervalInput] = useState('1');
  const [dateTimeFirmwareInput, setDateTimeFirmwareInput] =
    useState<string>(START);
  const [transactionIdInput, setTransactionIdInput] = useState<any>('');
  const [resetTypeInput, setResetTypeInput] = useState<any>('');
  const [loading, setLoading] = useState<any>(false);
  const [confKeyList, setConfKeyList] = useState<any>([]);
  const [keyTypeInput, setKeyTypeInput] = useState<any>(null);
  const [confKeyInput, setConfKeyInput] = useState<any>(null);
  const [customConfKeyInput, setCustomConfKeyInput] = useState<any>(null);
  const [confValueInput, setConfValueInput] = useState<any>('');
  const [triggerMessageInput, setTriggerMessageInput] = useState<any>('');
  const [profileInput, setProfileInput] = useState<any>('');
  const [cpDetailList, setCpDetailList] = useState<any>([]);
  const [identificationTypeRadioValue, setIdentificationTypeRadioValue] =
    useState<any>('OCPP_TAG');
  const [paymentTypes, setPaymentTypes] = useState<any>([]);
  const [paymentTypeInput, setPaymentTypeInput] = useState<any>(null);
  const [generatePayment, setGeneratePayment] = useState<any>(false);
  const [startDiagnosticsInput, setStartDiagnosticsInput] = useState<any>(null);
  const [stopDiagnosticsInput, setStopDiagnosticsInput] = useState<any>(null);
  const [filterTypeInput, setFilterTypeInput] =
    useState<any>('ChargingProfileId');
  const [chargingProfilePurposeInput, setChargingProfilePurposeInput] =
    useState<any>(null);
  const [stackLevelInput, setStackLevelInput] = useState<any>(null);
  const [dataInput, setDataInput] = useState<any>(null);
  const [messageIdInput, setMessageIdInput] = useState<any>(null);
  const [vendorIdInput, setVendorIdInput] = useState<any>(null);
  const [ocppIdTagInputTag, setOcppIdTagInputTag] = useState<any>(undefined);
  const [ocppIdTagInputDoc, setOcppIdTagInputDoc] = useState<any>(undefined);
  const [tagsList, setTagsList] = useState<any>([]);
  const [fetching, setFetching] = useState(false);
  const [addUpdateListInput, setAddUpdateListInput] = useState('');
  const [deleteListInput, setDeleteListInput] = useState('');
  const [updateTypeInput, setUpdateTypeInput] = useState<
    'DIFFERENTIAL' | 'FULL'
  >('FULL');
  const [listVersionInput, setListVersionInput] = useState<number>(0);
  const [addIdentificationType, setAddIdentificationType] =
    useState<IdentificationType>('OCPP_TAG');
  const [deleteIdentificationType, setDeleteIdentificationType] =
    useState<IdentificationType>('OCPP_TAG');

  const [selectedAddTag, setSelectedAddTag] = useState<string | undefined>(
    undefined,
  );
  const [selectedAddDocument, setSelectedAddDocument] = useState<
    string | undefined
  >(undefined);

  const [selectedDeleteTag, setSelectedDeleteTag] = useState<
    string | undefined
  >(undefined);
  const [selectedDeleteDocument, setSelectedDeleteDocument] = useState<
    string | undefined
  >(undefined);
  const [reserveNowIdentificationType, setReserveNowIdentificationType] =
    useState<IdentificationType>('OCPP_TAG');
  const [reserveNowIdTagInputTag, setReserveNowIdTagInputTag] = useState<any>(
    undefined,
  );
  const [reserveNowIdTagInputDoc, setReserveNowIdTagInputDoc] = useState<any>(
    undefined,
  );
  const [reserveNowExpiryMinutes, setReserveNowExpiryMinutes] =
    useState<number>(1);
  const fetchRef = useRef(0);

  const setAllConfList = useCallback(() => {
    if (dataSource?.ocppConfKeys) {
      setConfKeyList(Object.keys(dataSource?.ocppConfKeys));
    }
  }, [dataSource?.ocppConfKeys]);

  const resetAllConfList = () => {
    setConfKeyList([]);
  };

  const getTags = async (searchTagInput) => {
    return await getStartOcppTags(searchTagInput);
  };

  const debounceFetcher = useMemo(() => {
    const loadOptions = (searchTagInput: string) => {
      if (!searchTagInput) {
        setTagsList([]);
        setFetching(false);
        return;
      }
      fetchRef.current += 1;
      const fetchId = fetchRef.current;
      setTagsList([]);
      setFetching(true);

      getTags(searchTagInput).then((results) => {
        if (fetchId !== fetchRef.current) {
          return;
        }
        setTagsList(results.ocpp_tag_list);
        setFetching(false);
      });
    };

    return debounce(loadOptions, DEBOUNCE_TIMEOUT);
  }, [getTags, DEBOUNCE_TIMEOUT]);

  useEffect(() => {
    if (dataSource.cpList?.length) {
      const chargePoints: any[] = dataSource.cpList;
      setCpDetailList(
        chargePoints.filter((item) => targetKeys.includes(item.chargeBoxId)),
      );
    }
  }, [targetKeys, dataSource.cpList]);

  useEffect(() => {
    if (menuKey == 'start') {
      (async () => {
        const { payment_type_tenant_list } = await getPaymentTypes();
        setPaymentTypes(payment_type_tenant_list);
      })();
    } else if (menuKey == 'get_configuration') {
      setAllConfList();
      resetAllConfList();
    }
  }, [menuKey, setAllConfList]);

  const parseLinesToList = (value: string): string[] =>
    value
      .split('\n')
      .map((item) => item.trim())
      .filter(Boolean);

  const appendLine = (currentValue: string, newValue?: string) => {
    if (!newValue) return currentValue;

    const currentList = parseLinesToList(currentValue);

    if (currentList.includes(newValue)) {
      return currentValue;
    }

    return currentValue.trim()
      ? `${currentValue.trim()}\n${newValue}`
      : newValue;
  };

  useEffect(() => {
    if (updateTypeInput === 'FULL') {
      setAddUpdateListInput('');
      setDeleteListInput('');
      setSelectedAddTag(undefined);
      setSelectedAddDocument(undefined);
      setSelectedDeleteTag(undefined);
      setSelectedDeleteDocument(undefined);
    }
  }, [updateTypeInput]);

  switch (menuKey) {
    case 'change_availability':
      return (
        <OperationsExecuteWrapper>
          <span>
            {T('Connector ID')} ({T('Optional')}):
          </span>
          <Select
            value={connectorIdInput}
            onChange={(value) => {
              setConnectorIdInput(value);
            }}
          >
            {targetKeys?.length &&
              Object.keys(dataSource?.connectorIds || {}).length &&
              dataSource.connectorIds[targetKeys[0]]?.map(
                (connectorId: string, index: number) => (
                  <Option key={index} value={connectorId}>
                    {connectorId}
                  </Option>
                ),
              )}
          </Select>

          <span>{T('Availability Type')}</span>
          <Select
            value={availabilityTypeInput}
            onChange={(value) => {
              setAvailabilityTypeInput(value);
            }}
          >
            {targetKeys?.length &&
              dataSource?.availabilityTypes?.map(
                (value: string, index: number) => (
                  <Option key={index} value={value}>
                    {value}
                  </Option>
                ),
              )}
          </Select>

          <Button
            onClick={async () => {
              setLoading(true);
              if (!targetKeys.length)
                message.error('Por favor, selecione uma estação.');
              else if (availabilityTypeInput === '')
                message.error(
                  `Por favor, preencha o campo "${T('Availability Type')}".`,
                );
              else {
                const data = await setChangeAvailability({
                  chargePointSelectList: cpDetailList,
                  availType: availabilityTypeInput,
                  connectorId: connectorIdInput,
                  mode,
                });
                setAvailabilityTypeInput('');
                setConnectorIdInput('');
                if (data.type === 'success')
                  message.success('Comando enviado.');
                else message.error('Error');
              }
              setLoading(false);
            }}
            disabled={loading}
          >
            Executar
          </Button>
        </OperationsExecuteWrapper>
      );
    case 'start':
      return (
        <OperationsExecuteWrapper>
          <span>{T('Connector ID')}:</span>
          <Select
            value={connectorIdInput}
            onChange={(value) => {
              setConnectorIdInput(value);
            }}
          >
            {targetKeys?.length &&
              Object.keys(dataSource?.connectorIds || {}).length &&
              dataSource?.connectorIds[targetKeys[0]]?.map(
                (connectorId: string, index: number) => (
                  <Option key={index} value={connectorId}>
                    {connectorId}
                  </Option>
                ),
              )}
          </Select>

          <SearchOcppTagOrDocument
            identificationType={identificationTypeRadioValue}
            onIdentificationTypeChange={(value) => {
              setIdentificationTypeRadioValue(value);
              setTagsList([]);
            }}
            selectedTagValue={ocppIdTagInputTag}
            onSelectedTagChange={setOcppIdTagInputTag}
            selectedDocumentValue={ocppIdTagInputDoc}
            onSelectedDocumentChange={setOcppIdTagInputDoc}
            tagsList={tagsList}
            fetching={fetching}
            onSearch={debounceFetcher}
          />

          {paymentTypes?.length > 0 &&
            (isMonetizationProUser() || isTerpelTenant()) && (
              <Checkbox
                className='checkbox-generate-payment'
                onChange={(e) => {
                  setGeneratePayment(e.target.checked);
                }}
              >
                {T('Generate payment on recharge')}
              </Checkbox>
            )}

          {generatePayment ? (
            <>
              <span>{T('Payment Type')}:</span>
              <Select
                value={paymentTypeInput}
                onChange={(value) => {
                  setPaymentTypeInput(value);
                }}
              >
                {targetKeys?.length &&
                  Object.keys(dataSource?.cpList || {}).length &&
                  dataSource?.cpList?.filter(
                    (item) =>
                      targetKeys?.includes(item.chargeBoxId) &&
                      item?.hasPayment == true,
                  ).length &&
                  paymentTypes?.map((paymentType, index) => (
                    <Option key={index} value={paymentType.payment_type}>
                      {paymentType.payment_type}
                    </Option>
                  ))}
              </Select>
            </>
          ) : null}

          <Button
            onClick={async () => {
              setLoading(true);
              const idTagValue =
                identificationTypeRadioValue === 'OCPP_TAG'
                  ? ocppIdTagInputTag
                  : ocppIdTagInputDoc;

              if (!targetKeys.length) {
                message.error('Por favor, selecione uma estação.');
              } else if (connectorIdInput === '' || idTagValue === '') {
                message.error(
                  'Por favor, preencha todos os parâmetros obrigatórios.',
                );
              } else {
                let cardUuid = null;
                if (paymentTypeInput === 'PAGBANK_CARD') {
                  const selectedTag = tagsList.find(
                    (t) => t.ocpp_id_tag === idTagValue,
                  );
                  const cardData = await getUserPrimaryCard(
                    selectedTag?.user_uuid,
                  );
                  cardUuid = cardData?.uuid ?? null;
                }

                const data = await setRemoteStartTransaction({
                  chargePointSelectList: cpDetailList,
                  connectorId: connectorIdInput,
                  idTag: idTagValue,
                  paymentType: paymentTypeInput,
                  mode,
                  directPayment:
                    paymentTypeInput === 'WALLET' ||
                    paymentTypeInput === 'PAGBANK_CARD',
                  cardUuid,
                });
                setConnectorIdInput('');
                setOcppIdTagInputTag('');
                setOcppIdTagInputDoc('');
                if (data.type === 'success') {
                  message.success('Comando enviado.');
                } else {
                  message.error('Error');
                }
              }
              setLoading(false);
            }}
            disabled={loading}
          >
            Executar
          </Button>
        </OperationsExecuteWrapper>
      );
    case 'stop':
      return (
        <OperationsExecuteWrapper>
          <span>Transaction ID:</span>
          <Select
            value={transactionIdInput}
            onChange={(value) => {
              setTransactionIdInput(value);
            }}
          >
            {targetKeys?.length &&
              Object.keys(dataSource?.transactionIds || {}).length &&
              dataSource?.transactionIds[targetKeys[0]]?.map(
                (transactionId: string, index: number) => (
                  <Option key={index} value={transactionId}>
                    {transactionId}
                  </Option>
                ),
              )}
          </Select>
          <Button
            onClick={async () => {
              setLoading(true);
              if (!targetKeys.length)
                message.error('Por favor, selecione uma estação.');
              else if (transactionIdInput === '')
                message.error(
                  'Por favor, preencha todos os parâmetros obrigatórios.',
                );
              else {
                const data = await setRemoteStopTransaction({
                  chargePointSelectList: cpDetailList,
                  transactionId: transactionIdInput,
                  mode,
                });
                setTransactionIdInput('');
                if (data.type === 'success')
                  message.success('Comando enviado.');
                else message.error('Error');
              }
              setLoading(false);
            }}
            disabled={loading}
          >
            Executar
          </Button>
        </OperationsExecuteWrapper>
      );
    case 'reset':
      return (
        <OperationsExecuteWrapper>
          <span>Reset type:</span>
          <Select
            value={resetTypeInput}
            onChange={(value) => {
              setResetTypeInput(value);
            }}
          >
            <Option key={1} value='HARD'>
              HARD
            </Option>
            <Option key={2} value='SOFT'>
              SOFT
            </Option>
          </Select>
          <Button
            onClick={async () => {
              setLoading(true);
              if (!targetKeys.length)
                message.error('Por favor, selecione uma estação.');
              else if (resetTypeInput === '')
                message.error(
                  'Por favor, preencha todos os parâmetros obrigatórios.',
                );
              else {
                const data = await setReset({
                  chargePointSelectList: cpDetailList,
                  resetType: resetTypeInput,
                  mode,
                });
                setResetTypeInput('');
                if (data.type === 'success')
                  message.success('Comando enviado.');
                else message.error('Error');
              }
              setLoading(false);
            }}
            disabled={loading}
          >
            Executar
          </Button>
        </OperationsExecuteWrapper>
      );
    case 'get_configuration':
      return (
        <OperationsExecuteWrapper>
          <span>{T('Conf Key List')}:</span>
          <Select
            mode='multiple'
            style={{
              width: '100%',
              maxHeight: '148px',
              maxWidth: '400px',
              overflowY: 'scroll',
            }}
            placeholder={T('Select')}
            value={confKeyList}
            onChange={(value) => {
              setConfKeyList(value);
            }}
          >
            {targetKeys?.length &&
              Object.keys(dataSource?.ocppConfKeys || {}).length &&
              Object.keys(dataSource?.ocppConfKeys)?.map(
                (value: string, index: number) => (
                  <Option key={index} value={value}>
                    {T(value)}
                  </Option>
                ),
              )}
          </Select>
          <Button
            onClick={() => {
              setAllConfList();
            }}
          >
            {T('Select all')}
          </Button>
          <Button
            onClick={() => {
              resetAllConfList();
            }}
          >
            {T('Reset selection')}
          </Button>
          <Button
            onClick={async () => {
              setLoading(true);
              if (!targetKeys.length)
                message.error('Por favor, selecione uma estação.');
              else {
                const data = await setGetConfiguration({
                  chargePointSelectList: cpDetailList,
                  confKeyList: confKeyList,
                  setConfKeyList: true,
                  mode,
                });
                if (data.type === 'success') {
                  message.success('Comando enviado.');
                  const taskData = await getChargeboxTask({
                    chargeBoxId: targetKeys[0],
                    taskId: data.data.taskId,
                  });
                  setOperationResultSource(taskData);
                } else message.error('Error');
              }
              setLoading(false);
              setIsExecuted(true);
            }}
            disabled={loading}
          >
            Executar
          </Button>
        </OperationsExecuteWrapper>
      );

    case 'change_configuration':
      return (
        <OperationsExecuteWrapper>
          <span>{T('Key Type')}</span>
          <Select
            value={keyTypeInput}
            onChange={(value) => {
              setKeyTypeInput(value);
            }}
          >
            <Option key={1} value='PREDEFINED'>
              Predefined
            </Option>
            <Option key={2} value='CUSTOM'>
              Custom
            </Option>
          </Select>

          <span>{T('Configuration Key')}</span>
          <Select
            value={confKeyInput}
            onChange={(value) => {
              setConfKeyInput(value);
            }}
            disabled={keyTypeInput == 'CUSTOM'}
          >
            {targetKeys?.length &&
              dataSource?.ocppConfKeys &&
              Object.entries(dataSource.ocppConfKeys)?.map(
                (value: any, index: any) => (
                  <Option key={index} value={value[0]}>
                    {value[1]}
                  </Option>
                ),
              )}
          </Select>

          <span>{T('Custom Configuration Key')}:</span>
          <Input
            name='customConfKey'
            value={customConfKeyInput}
            onChange={(e) => {
              setCustomConfKeyInput(e.target.value);
            }}
            disabled={keyTypeInput == 'PREDEFINED'}
          />

          <span>{T('Value')}:</span>
          <Input
            name='confValue'
            value={confValueInput}
            onChange={(e) => {
              setConfValueInput(e.target.value);
            }}
          />

          <Button
            onClick={async () => {
              setLoading(true);
              if (!targetKeys.length)
                message.error('Por favor, selecione uma estação.');
              else if (keyTypeInput == null || confValueInput === '')
                message.error(
                  'Por favor, preencha todos os parâmetros obrigatórios.',
                );
              else {
                const data = await setChangeConfiguration({
                  chargePointSelectList: cpDetailList,
                  keyType: keyTypeInput,
                  confKey: confKeyInput,
                  customConfKey: customConfKeyInput,
                  value: confValueInput,
                  mode,
                });
                if (data.type === 'success') {
                  message.success('Comando enviado.');
                  const taskData = await getChargeboxTask({
                    chargeBoxId: targetKeys[0],
                    taskId: data.data.taskId,
                  });
                  setOperationResultSource(taskData);
                } else message.error('Error');
              }
              setLoading(false);
              setIsExecuted(true);
            }}
            disabled={loading}
          >
            Executar
          </Button>
        </OperationsExecuteWrapper>
      );

    case 'clear_cache':
      return (
        <OperationsExecuteWrapper>
          <Button
            onClick={async () => {
              setLoading(true);
              if (!targetKeys.length)
                message.error('Por favor, selecione uma estação.');
              else {
                const data = await setClearCache({
                  chargePointSelectList: cpDetailList,
                  mode,
                });
                if (data.type === 'success')
                  message.success('Comando enviado.');
                else message.error('Error');
              }
              setLoading(false);
            }}
            disabled={loading}
          >
            Executar
          </Button>
        </OperationsExecuteWrapper>
      );

    case 'get_diagnostics':
      return (
        <OperationsExecuteWrapper>
          <span>{T('Directory URI')}:</span>
          <Input
            name='directoryURIInput'
            value={directoryURIInput}
            onChange={(e) => {
              setDirectoryURIInput(e.target.value);
            }}
          />
          <span>{T('Retries (integer)')}:</span>
          <Input
            name='retriesInput'
            value={retriesInput}
            onChange={(e) => {
              setRetriesInput(e.target.value);
            }}
            placeholder={T('Optional')}
          />
          <span>{T('Retry Interval (integer)')}:</span>
          <Input
            name='retryIntervalInput'
            value={retryIntervalInput}
            onChange={(e) => {
              setRetryIntervalInput(e.target.value);
            }}
            placeholder={T('Optional')}
          />
          <span>{T('Start Date/Time')}:</span>
          <DatePickerCustom
            format='DD/MM/YYYY HH:mm'
            className='datetime_picker'
            showTime={{
              hideDisabledOptions: true,
              defaultValue: moment(),
            }}
            placeholder={T('Optional')}
            allowClear={false}
            value={startDiagnosticsInput}
            onChange={(date: any) => {
              setStartDiagnosticsInput(date);
            }}
          />
          <span>{T('Stop Date/Time')}:</span>
          <DatePickerCustom
            format='DD/MM/YYYY HH:mm'
            className='datetime_picker'
            showTime={{
              hideDisabledOptions: true,
              defaultValue: moment(),
            }}
            placeholder={T('Optional')}
            allowClear={false}
            value={stopDiagnosticsInput}
            onChange={(date: any) => {
              setStopDiagnosticsInput(date);
            }}
          />

          <Button
            onClick={async () => {
              setLoading(true);
              if (!targetKeys.length)
                message.error('Por favor, selecione uma estação.');
              else if (directoryURIInput === '')
                message.error(
                  'Por favor, preencha todos os parâmetros obrigatórios.',
                );
              else {
                const data = await setGetDiagnostics({
                  chargePointSelectList: cpDetailList,
                  location: directoryURIInput,
                  retries: retriesInput === '' ? 1 : Number(retriesInput),
                  retryInterval:
                    retryIntervalInput === '' ? 1 : Number(retryIntervalInput),
                  start:
                    startDiagnosticsInput != null
                      ? moment(startDiagnosticsInput).utc().format()
                      : null,
                  stop:
                    stopDiagnosticsInput != null
                      ? moment(stopDiagnosticsInput).utc().format()
                      : null,
                  mode,
                });
                setDirectoryURIInput('');
                if (data.type === 'success')
                  message.success('Comando enviado.');
                else message.error('Error');
              }
              setLoading(false);
            }}
            disabled={loading}
          >
            Executar
          </Button>
        </OperationsExecuteWrapper>
      );
    case 'unlock_connector':
      return (
        <OperationsExecuteWrapper>
          <span>{T('Connector ID')}:</span>
          <Select
            value={connectorIdInput}
            onChange={(value) => {
              setConnectorIdInput(value);
            }}
          >
            {targetKeys?.length &&
              Object.keys(dataSource?.connectorIds || {}).length &&
              dataSource.connectorIds[targetKeys[0]]?.map(
                (connectorId: string, index: number) => (
                  <Option key={index} value={connectorId}>
                    {connectorId}
                  </Option>
                ),
              )}
          </Select>
          <Button
            onClick={async () => {
              setLoading(true);
              if (!targetKeys.length)
                message.error('Por favor, selecione uma estação.');
              else if (connectorIdInput === '')
                message.error(
                  `Por favor, preencha o campo "${T('Connector ID')}".`,
                );
              else {
                const data = await setUnlockConnector({
                  chargePointSelectList: cpDetailList,
                  connectorId: connectorIdInput,
                  mode,
                });
                setConnectorIdInput('');
                if (data.type === 'success')
                  message.success('Comando enviado.');
                else message.error('Error');
              }
              setLoading(false);
            }}
            disabled={loading}
          >
            Executar
          </Button>
        </OperationsExecuteWrapper>
      );
    case 'update_firmware':
      return (
        <OperationsExecuteWrapper>
          <span>{T('Directory URI')}:</span>
          <Input
            name='directoryURIInput'
            value={directoryURIInput}
            onChange={(e) => {
              setDirectoryURIInput(e.target.value);
            }}
          />
          <span>{T('Retries (integer)')}:</span>
          <Input
            name='retriesInput'
            value={retriesInput}
            onChange={(e) => {
              setRetriesInput(e.target.value);
            }}
            placeholder={T('Optional')}
          />
          <span>{T('Retry Interval (integer)')}:</span>
          <Input
            name='retryIntervalInput'
            value={retryIntervalInput}
            onChange={(e) => {
              setRetryIntervalInput(e.target.value);
            }}
            placeholder={T('Optional')}
          />
          <span>{T('Retrieve DateTime Firmware')}:</span>
          <DatePickerCustom
            format='DD/MM/YYYY HH:mm'
            className='datetime_picker'
            showTime={{
              hideDisabledOptions: true,
              defaultValue: moment(),
            }}
            allowClear={false}
            value={moment(dateTimeFirmwareInput)}
            onChange={(date: any) => {
              setDateTimeFirmwareInput(
                moment(date).format('YYYY-MM-DD HH:mm:ss'),
              );
            }}
          />

          <Button
            onClick={async () => {
              setLoading(true);
              if (!targetKeys.length)
                message.error('Por favor, selecione uma estação.');
              else if (directoryURIInput === '' || dateTimeFirmwareInput === '')
                message.error(
                  'Por favor, preencha todos os parâmetros obrigatórios.',
                );
              else {
                const data = await setUpdateFirmware({
                  chargePointSelectList: cpDetailList,
                  location: directoryURIInput,
                  retries: retriesInput === '' ? 1 : Number(retriesInput),
                  retryInterval:
                    retryIntervalInput === '' ? 1 : Number(retryIntervalInput),
                  retrieve: moment(dateTimeFirmwareInput).utc().format(),
                  mode,
                });
                setDirectoryURIInput('');
                if (data.type === 'success')
                  message.success('Comando enviado.');
                else message.error('Error');
              }
              setLoading(false);
            }}
            disabled={loading}
          >
            Executar
          </Button>
        </OperationsExecuteWrapper>
      );
    case 'data_transfer':
      return (
        <OperationsExecuteWrapper>
          <span>{T('Vendor ID')}:</span>
          <Input
            name='vendorIdInput'
            value={vendorIdInput}
            onChange={(e) => {
              setVendorIdInput(e.target.value);
            }}
          />
          <span>{T('Message ID')}:</span>
          <Input
            name='messageIdInput'
            value={messageIdInput}
            onChange={(e) => {
              setMessageIdInput(e.target.value);
            }}
            placeholder={T('Optional')}
          />
          <span>{T('Data')}:</span>
          <Input
            name='dataInput'
            value={dataInput}
            onChange={(e) => {
              setDataInput(e.target.value);
            }}
            placeholder={T('Optional')}
          />

          <Button
            onClick={async () => {
              setLoading(true);
              if (!targetKeys.length)
                message.error('Por favor, selecione uma estação.');
              else if (vendorIdInput === '')
                message.error(
                  'Por favor, preencha todos os parâmetros obrigatórios.',
                );
              else {
                const dataOut = await setDataTransfer({
                  chargePointSelectList: cpDetailList,
                  vendorId: vendorIdInput,
                  messageId: messageIdInput === '' ? null : messageIdInput,
                  data: dataInput === '' ? null : dataInput,
                  mode,
                });
                setDirectoryURIInput('');
                if (dataOut.type === 'success')
                  message.success('Comando enviado.');
                else message.error('Error');
              }
              setLoading(false);
            }}
            disabled={loading}
          >
            Executar
          </Button>
        </OperationsExecuteWrapper>
      );
    case 'trigger_message':
      return (
        <OperationsExecuteWrapper>
          <span>{T('Trigger Message')}</span>
          <Select
            value={triggerMessageInput}
            onChange={(value) => {
              setTriggerMessageInput(value);
            }}
          >
            {targetKeys?.length &&
              dataSource?.triggerMessageTypes?.map(
                (value: string, index: number) => (
                  <Option key={index} value={value}>
                    {value}
                  </Option>
                ),
              )}
          </Select>

          <span>
            {T('Connector ID')} ({T('Optional')}):
          </span>
          <Select
            value={connectorIdInput}
            onChange={(value) => {
              setConnectorIdInput(value);
            }}
          >
            {targetKeys?.length &&
              Object.keys(dataSource?.connectorIds || {}).length &&
              dataSource.connectorIds[targetKeys[0]]?.map(
                (connectorId: string, index: number) => (
                  <Option key={index} value={connectorId}>
                    {connectorId}
                  </Option>
                ),
              )}
          </Select>
          <Button
            onClick={async () => {
              setLoading(true);
              if (!targetKeys.length)
                message.error('Por favor, selecione uma estação.');
              else if (triggerMessageInput === '')
                message.error(
                  `Por favor, preencha o campo "${T('Trigger Message')}".`,
                );
              else {
                const data = await setTriggerMessage({
                  chargePointSelectList: cpDetailList,
                  triggerMessage: triggerMessageInput,
                  connectorId: connectorIdInput,
                  mode,
                });
                setTriggerMessageInput('');
                setConnectorIdInput('');
                if (data.type === 'success')
                  message.success('Comando enviado.');
                else message.error('Error');
              }
              setLoading(false);
            }}
            disabled={loading || mode === 'v1.5'}
          >
            Executar
          </Button>
        </OperationsExecuteWrapper>
      );
    case 'set_charging_profile':
      return (
        <OperationsExecuteWrapper>
          <span>{T('Charging Profile ID')}:</span>
          <Select
            style={{ width: '100%', maxHeight: '148px', maxWidth: '400px' }}
            placeholder={T('Select')}
            value={profileInput}
            onChange={(value) => {
              setProfileInput(value);
            }}
          >
            {dataSource.profileList?.length &&
              dataSource.profileList?.map(
                (item: {
                  chargingProfilePk: number;
                  description: string;
                  itemDescription: string;
                }) => (
                  <Option
                    key={item.chargingProfilePk}
                    value={item.chargingProfilePk}
                  >
                    {`${item.chargingProfilePk} - ${T(item.description)}`}
                  </Option>
                ),
              )}
          </Select>
          <span>{T('Connector ID')}:</span>
          <Select
            value={connectorIdInput}
            onChange={(value) => {
              setConnectorIdInput(value);
            }}
          >
            <Option key={0} value={0}>
              {T('All connectors')}
            </Option>
            {targetKeys?.length &&
              Object.keys(dataSource?.connectorIds || {}).length &&
              dataSource?.connectorIds[targetKeys[0]]?.map(
                (connectorId: string, index: number) => (
                  <Option key={index} value={connectorId}>
                    {connectorId}
                  </Option>
                ),
              )}
          </Select>
          <Button
            onClick={async () => {
              setLoading(true);
              if (!targetKeys.length)
                message.error('Por favor, selecione uma estação.');
              else if (profileInput === '')
                message.error(
                  'Por favor, preencha todos os parâmetros obrigatórios.',
                );
              else if (connectorIdInput === '')
                message.error(
                  'Por favor, preencha todos os parâmetros obrigatórios.',
                );
              else {
                const data = await setSetChargingProfile({
                  chargePointSelectList: cpDetailList,
                  chargingProfilePk: profileInput,
                  connectorId: connectorIdInput,
                  mode,
                });
                setConnectorIdInput('');
                if (data.type === 'success')
                  message.success('Comando enviado.');
                else message.error('Error');
              }
              setLoading(false);
              setIsExecuted(true);
            }}
            disabled={loading || mode === 'v1.5'}
          >
            Executar
          </Button>
        </OperationsExecuteWrapper>
      );
    case 'clear_charging_profile':
      return (
        <OperationsExecuteWrapper>
          <span>{T('Filter Type')}</span>
          <Select
            value={filterTypeInput}
            onChange={(value) => {
              setFilterTypeInput(value);
            }}
          >
            <Option key={1} value='ChargingProfileId'>
              {T('Charging Profile ID')}
            </Option>
            <Option key={2} value='OtherParameters'>
              {T('Other Parameters')}
            </Option>
          </Select>
          <span>{T('Charging Profile ID')}:</span>
          <Select
            style={{ width: '100%', maxHeight: '148px', maxWidth: '400px' }}
            placeholder={T('Select')}
            value={profileInput}
            onChange={(value) => {
              setProfileInput(value);
            }}
            disabled={filterTypeInput == 'OtherParameters'}
          >
            {dataSource.profileList?.length &&
              dataSource.profileList?.map(
                (item: {
                  chargingProfilePk: number;
                  description: string;
                  itemDescription: string;
                }) => (
                  <Option
                    key={item.chargingProfilePk}
                    value={item.chargingProfilePk}
                  >
                    {`${item.chargingProfilePk} - ${T(item.description)}`}
                  </Option>
                ),
              )}
          </Select>
          <span>{T('Connector ID')}:</span>
          <Select
            value={connectorIdInput}
            onChange={(value) => {
              setConnectorIdInput(value);
            }}
            disabled={filterTypeInput == 'ChargingProfileId'}
          >
            {targetKeys?.length &&
              Object.keys(dataSource?.connectorIds || {}).length &&
              dataSource?.connectorIds[targetKeys[0]]?.map(
                (connectorId: string, index: number) => (
                  <Option key={index} value={connectorId}>
                    {connectorId}
                  </Option>
                ),
              )}
          </Select>

          <span>{T('Charging Profile Purpose')}</span>
          <Select
            value={chargingProfilePurposeInput}
            onChange={(value) => {
              setChargingProfilePurposeInput(value);
            }}
            disabled={filterTypeInput == 'ChargingProfileId'}
          >
            <Option key={1} value='CHARGE_POINT_MAX_PROFILE'>
              {T('CHARGE_POINT_MAX_PROFILE')}
            </Option>
            <Option key={2} value='TX_DEFAULT_PROFILE'>
              {T('TX_DEFAULT_PROFILE')}
            </Option>
            <Option key={3} value='TX_PROFILE'>
              {T('TX_PROFILE')}
            </Option>
          </Select>

          <span>{T('Stack Level (integer)')}:</span>
          <Input
            name='stackLevelInput'
            value={stackLevelInput}
            onChange={(e) => {
              setStackLevelInput(e.target.value);
            }}
            disabled={filterTypeInput == 'ChargingProfileId'}
          />

          <Button
            onClick={async () => {
              setLoading(true);
              if (!targetKeys.length)
                message.error('Por favor, selecione uma estação.');
              else {
                if (filterTypeInput == 'OtherParameters') {
                  setProfileInput(null);
                }
                const data = await setClearChargingProfile({
                  chargePointSelectList: cpDetailList,
                  filterType: filterTypeInput,
                  chargingProfilePurpose: chargingProfilePurposeInput,
                  stackLevel:
                    stackLevelInput != null ? Number(stackLevelInput) : null,
                  chargingProfilePk: profileInput,
                  connectorId: connectorIdInput,
                  mode,
                });
                setConnectorIdInput(null);
                setChargingProfilePurposeInput(null);
                setStackLevelInput(null);
                setProfileInput(null);
                if (data.type === 'success')
                  message.success('Comando enviado.');
                else message.error('Error');
              }
              setLoading(false);
              setIsExecuted(true);
            }}
            disabled={loading || mode === 'v1.5'}
          >
            Executar
          </Button>
        </OperationsExecuteWrapper>
      );
    case 'send_local_list':
      return (
        <OperationsExecuteWrapper>
          <span>{T('List Version')}:</span>
          <Input
            type='number'
            value={listVersionInput}
            onChange={(e) => {
              setListVersionInput(Number(e.target.value) || 0);
            }}
            min={0}
          />

          <span>{T('Update Type')}:</span>
          <Select
            value={updateTypeInput}
            onChange={(value) => {
              setUpdateTypeInput(value);
            }}
          >
            <Option value='FULL'>{T('Full')}</Option>
            <Option value='DIFFERENTIAL'>{T('Differential')}</Option>
          </Select>

          <SearchOcppTagOrDocument
            label={T('Search item to add/update')}
            identificationType={addIdentificationType}
            onIdentificationTypeChange={(value) => {
              setAddIdentificationType(value);
              setTagsList([]);
            }}
            selectedTagValue={selectedAddTag}
            onSelectedTagChange={setSelectedAddTag}
            selectedDocumentValue={selectedAddDocument}
            onSelectedDocumentChange={setSelectedAddDocument}
            tagsList={tagsList}
            fetching={fetching}
            onSearch={debounceFetcher}
            disabled={updateTypeInput === 'FULL'}
          />

          <Button
            onClick={() => {
              const addValue =
                addIdentificationType === 'OCPP_TAG'
                  ? selectedAddTag
                  : selectedAddDocument;

              setAddUpdateListInput((prev) => appendLine(prev, addValue));
              setSelectedAddTag(undefined);
              setSelectedAddDocument(undefined);
            }}
            disabled={
              updateTypeInput === 'FULL' ||
              !(addIdentificationType === 'OCPP_TAG'
                ? selectedAddTag
                : selectedAddDocument)
            }
          >
            {T('Add to list')}
          </Button>

          <span>{T('Add / Update List')}:</span>
          <TextArea
            value={addUpdateListInput}
            onChange={(e) => {
              setAddUpdateListInput(e.target.value);
            }}
            rows={5}
            autoSize={{ minRows: 5, maxRows: 10 }}
            placeholder={T('One tag per line')}
            disabled={updateTypeInput === 'FULL'}
          />

          <SearchOcppTagOrDocument
            label={T('Search item to delete')}
            identificationType={deleteIdentificationType}
            onIdentificationTypeChange={(value) => {
              setDeleteIdentificationType(value);
              setTagsList([]);
            }}
            selectedTagValue={selectedDeleteTag}
            onSelectedTagChange={setSelectedDeleteTag}
            selectedDocumentValue={selectedDeleteDocument}
            onSelectedDocumentChange={setSelectedDeleteDocument}
            tagsList={tagsList}
            fetching={fetching}
            onSearch={debounceFetcher}
            disabled={updateTypeInput === 'FULL'}
          />

          <Button
            onClick={() => {
              const deleteValue =
                deleteIdentificationType === 'OCPP_TAG'
                  ? selectedDeleteTag
                  : selectedDeleteDocument;

              setDeleteListInput((prev) => appendLine(prev, deleteValue));
              setSelectedDeleteTag(undefined);
              setSelectedDeleteDocument(undefined);
            }}
            disabled={
              updateTypeInput === 'FULL' ||
              !(deleteIdentificationType === 'OCPP_TAG'
                ? selectedDeleteTag
                : selectedDeleteDocument)
            }
          >
            {T('Add to list')}
          </Button>

          <span>{T('Delete List')}:</span>
          <TextArea
            value={deleteListInput}
            onChange={(e) => {
              setDeleteListInput(e.target.value);
            }}
            rows={5}
            autoSize={{ minRows: 5, maxRows: 10 }}
            placeholder={T('One tag per line')}
            disabled={updateTypeInput === 'FULL'}
          />

          <Button
            onClick={async () => {
              setLoading(true);

              if (!targetKeys.length) {
                message.error('Por favor, selecione uma estação.');
              } else if (
                updateTypeInput === 'DIFFERENTIAL' &&
                !addUpdateListInput &&
                !deleteListInput
              ) {
                message.error('Por favor, selecione uma tag.');
              } else {
                const addUpdateList = parseLinesToList(addUpdateListInput);
                const deleteList = parseLinesToList(deleteListInput);

                const data = await setSendLocalList({
                  chargePointSelectList: cpDetailList,
                  addUpdateList,
                  deleteList,
                  listVersion: listVersionInput,
                  updateType: updateTypeInput,
                  mode,
                });

                if (data.type === 'success') {
                  message.success('Comando enviado.');
                  setAddUpdateListInput('');
                  setDeleteListInput('');
                  setListVersionInput(0);
                  setSelectedAddTag(undefined);
                  setSelectedDeleteTag(undefined);
                  setUpdateTypeInput('FULL');
                } else {
                  message.error('Error');
                }
              }

              setLoading(false);
            }}
            disabled={loading}
          >
            Executar
          </Button>
        </OperationsExecuteWrapper>
      );
    case 'get_composite_schedule':
      return (
        <OperationsExecuteWrapper>
          <span>{T('Connector ID')}:</span>
          <Input
            name='connectorIdInput'
            type='number'
            value={connectorIdInput}
            onChange={(e) => {
              setConnectorIdInput(e.target.value);
            }}
            placeholder={T('0 = charge point as a whole')}
          />
          <span>{T('Duration (in seconds)')}:</span>
          <Input
            name='durationInput'
            type='number'
            value={durationInput}
            onChange={(e) => {
              setDurationInput(e.target.value);
            }}
          />
          <span>{T('Charging Rate Unit')}:</span>
          <Select
            value={chargingRateUnitInput}
            onChange={(value) => {
              setChargingRateUnitInput(value);
            }}
          >
            <Option value=''>{T('-- Empty --')}</Option>
            <Option value='W'>W</Option>
            <Option value='A'>A</Option>
          </Select>
          <Button
            onClick={async () => {
              setLoading(true);
              if (!targetKeys.length)
                message.error('Por favor, selecione uma estação.');
              else if (connectorIdInput === '' || durationInput === '')
                message.error(
                  'Por favor, preencha todos os parâmetros obrigatórios.',
                );
              else {
                const data = await setGetCompositeSchedule({
                  chargePointSelectList: cpDetailList,
                  connectorId: Number(connectorIdInput),
                  durationInSeconds: Number(durationInput),
                  chargingRateUnit:
                    chargingRateUnitInput === ''
                      ? undefined
                      : chargingRateUnitInput,
                  mode,
                });
                setConnectorIdInput('');
                setDurationInput('');
                setChargingRateUnitInput('');
                if (data.type === 'success')
                  message.success('Comando enviado.');
                else message.error('Error');
              }
              setLoading(false);
            }}
            disabled={loading}
          >
            Executar
          </Button>
        </OperationsExecuteWrapper>
      );
    case 'reserve_now':
      return (
        <OperationsExecuteWrapper>
          <span>{T('Connector ID')}:</span>
          <Select
            value={connectorIdInput}
            onChange={(value) => {
              setConnectorIdInput(value);
            }}
          >
            {targetKeys?.length &&
              Object.keys(dataSource?.connectorIds || {}).length &&
              dataSource?.connectorIds[targetKeys[0]]?.map(
                (connectorId: string, index: number) => (
                  <Option key={index} value={connectorId}>
                    {connectorId}
                  </Option>
                ),
              )}
          </Select>

          <SearchOcppTagOrDocument
            identificationType={reserveNowIdentificationType}
            onIdentificationTypeChange={(value) => {
              setReserveNowIdentificationType(value);
              setTagsList([]);
            }}
            selectedTagValue={reserveNowIdTagInputTag}
            onSelectedTagChange={setReserveNowIdTagInputTag}
            selectedDocumentValue={reserveNowIdTagInputDoc}
            onSelectedDocumentChange={setReserveNowIdTagInputDoc}
            tagsList={tagsList}
            fetching={fetching}
            onSearch={debounceFetcher}
          />

          <span>{T('Expiry (minutes)')}:</span>
          <InputNumber
            min={1}
            value={reserveNowExpiryMinutes}
            onChange={(value) => {
              setReserveNowExpiryMinutes(Number(value));
            }}
          />

          <Button
            onClick={async () => {
              setLoading(true);
              const idTagValue =
                reserveNowIdentificationType === 'OCPP_TAG'
                  ? reserveNowIdTagInputTag
                  : reserveNowIdTagInputDoc;

              if (!targetKeys.length) {
                message.error('Por favor, selecione uma estação.');
              } else if (
                connectorIdInput === '' ||
                !idTagValue ||
                !reserveNowExpiryMinutes ||
                reserveNowExpiryMinutes < 1
              ) {
                message.error(
                  'Por favor, preencha todos os parâmetros obrigatórios.',
                );
              } else {
                const data = await setReserveNow({
                  chargePointSelectList: cpDetailList,
                  connectorId: connectorIdInput,
                  expiryMinutes: reserveNowExpiryMinutes,
                  idTag: idTagValue,
                  mode,
                });
                if (data.type === 'success') {
                  message.success('Comando enviado.');
                  setConnectorIdInput('');
                  setReserveNowIdTagInputTag(undefined);
                  setReserveNowIdTagInputDoc(undefined);
                  setReserveNowExpiryMinutes(1);
                } else {
                  message.error('Error');
                }
              }
              setLoading(false);
            }}
            disabled={loading}
          >
            Executar
          </Button>
        </OperationsExecuteWrapper>
      );
    default:
      return null;
  }
};

const OperationsExecuteWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  border: 1px solid ${COLORS.stone};
  border-radius: 2px;
  margin: 0.5em 1em 0.5em 0;
  padding: 1em;
  min-width: 400px;
  max-width: 500px;
  background-color: #fff;

  > span:not(:first-child) {
    margin-top: 0.5em;
  }

  .ant-select {
    width: 100%;
  }

  button {
    margin-top: 1em;
  }

  .checkbox-generate-payment {
    margin-top: 10px;
  }
`;

export default OperationsExecute;
